import type { SiteTemplateDefinition, TemplateRenderProps } from "../types";
import { renderRestaurantTemplate, restaurantModernTemplate } from "./restaurantModern";

function BrunchStyles() {
    return (
        <style>{`
            [data-web-template="restaurant-brunch"] {
                --brunch-cream: var(--site-background);
                --brunch-paper: var(--site-surface);
                --brunch-ink: var(--site-on-background);
                --brunch-leaf: var(--site-primary);
                --brunch-yolk: var(--site-accent);
                --brunch-peach: #ffad91;
                --brunch-tomato: #ed684a;
                --brunch-mint: #cfe8d3;
                --brunch-sky: #b9dfe0;
                --brunch-display-font: ui-rounded, "Arial Rounded MT Bold", "Trebuchet MS", system-ui, sans-serif;
                background: var(--brunch-cream);
            }

            [data-web-template="restaurant-brunch"] :where(
                .site-builder-text-h1,
                .site-builder-text-h2,
                .site-builder-text-h3,
                .site-builder-card-title
            ) {
                font-family: var(--brunch-display-font) !important;
                font-style: normal !important;
                font-weight: 900 !important;
                letter-spacing: -.045em !important;
            }

            [data-web-template="restaurant-brunch"] .site-builder-section {
                overflow: hidden;
            }

            /* A bright café header replaces the inherited cinematic overlay. */
            [data-web-template="restaurant-brunch"] [data-web-region="header"],
            [data-web-template="restaurant-brunch"] [data-web-region="header"][data-restaurant-header-scrolled] {
                --site-text: var(--brunch-ink);
                --site-muted: color-mix(in srgb, var(--brunch-ink) 67%, transparent);
                backdrop-filter: blur(18px) saturate(1.15) !important;
                background: color-mix(in srgb, var(--brunch-cream) 94%, transparent) !important;
                border-bottom: 3px solid var(--brunch-tomato) !important;
                box-shadow: 0 7px 0 var(--brunch-yolk), 0 14px 34px rgb(33 59 45 / .12) !important;
                color: var(--brunch-ink) !important;
                min-height: 82px !important;
            }

            [data-web-template="restaurant-brunch"] [data-web-region="header"] > .site-builder-grid {
                align-items: center;
            }

            [data-web-template="restaurant-brunch"] [data-web-region="header"] [data-document-role="brandCopy"] {
                background: transparent !important;
                border-radius: 0 !important;
                padding: 0 !important;
            }

            [data-web-template="restaurant-brunch"] [data-web-region="header"] [data-document-role="brand"] > [data-site-builder-block="badge"] {
                background: var(--brunch-yolk) !important;
                border: 2px solid var(--brunch-ink) !important;
                box-shadow: 3px 3px 0 var(--brunch-tomato) !important;
                color: var(--brunch-ink) !important;
                transform: rotate(-7deg);
            }

            [data-web-template="restaurant-brunch"] [data-web-region="header"] :where(
                [data-document-role="brandCopy"],
                [data-document-role="headerNavigation"]
            ) :where(.site-builder-text, .site-builder-text > span) {
                color: var(--brunch-ink) !important;
            }

            [data-web-template="restaurant-brunch"] [data-web-region="header"] [data-document-role="brandCopy"] .site-builder-text,
            [data-web-template="restaurant-brunch"] [data-web-region="header"] [data-document-role="brandCopy"] .site-builder-text > span {
                color: var(--brunch-ink) !important;
            }

            [data-web-template="restaurant-brunch"] [data-web-region="header"] [data-document-role="brandCopy"] > [data-site-builder-block="text"]:first-child .site-builder-text > span {
                font-family: var(--brunch-display-font) !important;
                font-size: 1.35rem !important;
                font-weight: 900 !important;
                letter-spacing: -.05em;
            }

            [data-web-template="restaurant-brunch"] [data-web-region="header"] [data-document-role="brandCopy"] > [data-site-builder-block="text"]:last-child :where(.site-builder-text, .site-builder-text > span) {
                color: var(--brunch-leaf) !important;
            }

            [data-web-template="restaurant-brunch"] [data-web-region="header"] [data-document-role="headerNavigation"] {
                background: var(--brunch-paper) !important;
                border: 2px solid color-mix(in srgb, var(--brunch-ink) 84%, transparent) !important;
                box-shadow: 4px 4px 0 var(--brunch-mint);
            }

            [data-web-template="restaurant-brunch"] [data-web-region="header"] [data-document-role="headerNavigation"] .site-builder-text::after {
                background: var(--brunch-tomato) !important;
                height: 4px !important;
            }

            [data-web-template="restaurant-brunch"] [data-web-region="header"] [data-document-role="headerCta"] .site-builder-button {
                background: var(--brunch-yolk) !important;
                border: 2px solid var(--brunch-ink) !important;
                box-shadow: 4px 4px 0 var(--brunch-tomato) !important;
                color: var(--brunch-ink) !important;
                transform: rotate(1.5deg);
            }

            [data-web-template="restaurant-brunch"] [data-web-region="header"] .site-navigation-root {
                color: var(--brunch-ink) !important;
            }

            [data-web-template="restaurant-brunch"] [data-web-region="header"] :where(
                .site-navigation-primary-trigger,
                .site-navigation-primary-link,
                .site-navigation-primary-toggle
            ) {
                color: var(--brunch-ink) !important;
                font-family: var(--site-body-font) !important;
                font-weight: 850 !important;
                text-shadow: none !important;
            }

            [data-web-template="restaurant-brunch"] [data-web-region="header"] :where(
                .site-navigation-primary-trigger,
                .site-navigation-primary-link
            ):hover {
                color: var(--brunch-tomato) !important;
            }

            /* Sunlit editorial hero with an actual image collage instead of a dark cover. */
            [data-web-template="restaurant-brunch"] [data-web-region="hero"] {
                --site-text: var(--brunch-ink);
                --site-muted: color-mix(in srgb, var(--brunch-ink) 68%, transparent);
                align-items: center !important;
                background:
                    radial-gradient(circle at 7% 24%, var(--brunch-yolk) 0 5.4rem, transparent 5.5rem),
                    radial-gradient(circle at 91% 16%, var(--brunch-peach) 0 8.5rem, transparent 8.6rem),
                    radial-gradient(circle at 82% 88%, var(--brunch-mint) 0 12rem, transparent 12.1rem),
                    linear-gradient(118deg, var(--brunch-cream), color-mix(in srgb, var(--brunch-yolk) 13%, var(--brunch-cream))) !important;
                color: var(--brunch-ink) !important;
                isolation: isolate;
                min-height: 900px !important;
                padding-block: clamp(10rem, 14vw, 12rem) clamp(7rem, 10vw, 9rem) !important;
            }

            [data-web-template="restaurant-brunch"] [data-web-region="hero"]::before {
                background:
                    repeating-linear-gradient(-12deg, transparent 0 17px, color-mix(in srgb, var(--brunch-tomato) 17%, transparent) 18px 21px, transparent 22px 39px) !important;
                clip-path: polygon(0 58%, 100% 35%, 100% 100%, 0 100%);
                content: "";
                inset: auto -8% 0 !important;
                height: 18rem;
                opacity: .46;
                position: absolute;
                transform: rotate(-2deg);
                z-index: -1 !important;
            }

            [data-web-template="restaurant-brunch"] [data-web-region="hero"]::after {
                background:
                    radial-gradient(circle at 14px -3px, transparent 15px, var(--brunch-tomato) 16px 18px, transparent 19px) 0 100% / 28px 28px repeat-x;
                bottom: 0;
                content: "";
                height: 30px;
                left: 0;
                pointer-events: none;
                position: absolute;
                right: 0;
                z-index: 3;
            }

            [data-web-template="restaurant-brunch"] [data-web-region="hero"] > .site-builder-grid {
                align-items: center !important;
                gap: clamp(2rem, 5vw, 5rem) !important;
                min-height: 0 !important;
                position: relative;
                z-index: 2;
            }

            [data-web-template="restaurant-brunch"] [data-web-region="hero"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="heroCopy"]) {
                grid-column: 1 / span 6 !important;
                grid-row: 1 !important;
            }

            [data-web-template="restaurant-brunch"] [data-web-region="hero"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="heroFeature"]) {
                grid-column: 7 / -1 !important;
                grid-row: 1 !important;
            }

            [data-web-template="restaurant-brunch"] [data-document-role="heroCopy"] {
                align-items: start !important;
                align-self: center;
                grid-column: 1 / span 6 !important;
                grid-row: 1 !important;
                text-align: left !important;
                transform: rotate(-1deg);
                z-index: 2;
            }

            [data-web-template="restaurant-brunch"] [data-document-role="heroHeading"] .site-builder-text {
                color: var(--brunch-leaf) !important;
                font-family: var(--brunch-display-font) !important;
                font-size: clamp(4.6rem, 8.2vw, 8.8rem) !important;
                font-style: normal !important;
                font-weight: 900 !important;
                letter-spacing: -.075em !important;
                line-height: .82 !important;
                max-width: 780px !important;
                text-shadow: 4px 5px 0 color-mix(in srgb, var(--brunch-yolk) 74%, transparent);
                text-wrap: balance;
            }

            [data-web-template="restaurant-brunch"] [data-document-role="heroDescription"] .site-builder-text {
                color: var(--brunch-ink) !important;
                font-size: clamp(1.05rem, 1.55vw, 1.28rem) !important;
                font-weight: 650 !important;
                line-height: 1.55 !important;
                max-width: 35rem !important;
                width: 100% !important;
            }

            [data-web-template="restaurant-brunch"] [data-document-role="heroPrimaryAction"],
            [data-web-template="restaurant-brunch"] [data-document-role="heroSecondaryAction"] {
                transition: transform 160ms ease !important;
            }

            [data-web-template="restaurant-brunch"] [data-document-role="heroPrimaryAction"] .site-builder-button,
            [data-web-template="restaurant-brunch"] [data-document-role="heroSecondaryAction"] .site-builder-button {
                border: 2px solid var(--brunch-ink) !important;
                border-radius: 999px !important;
                box-shadow: 5px 6px 0 var(--brunch-ink) !important;
                color: var(--brunch-ink) !important;
                font-size: .78rem !important;
                min-height: 58px !important;
                transition: box-shadow 160ms ease, transform 160ms ease !important;
            }

            [data-web-template="restaurant-brunch"] [data-document-role="heroPrimaryAction"] .site-builder-button {
                background: var(--brunch-yolk) !important;
            }

            [data-web-template="restaurant-brunch"] [data-document-role="heroPrimaryAction"] {
                transform: rotate(-1deg);
            }

            [data-web-template="restaurant-brunch"] [data-document-role="heroSecondaryAction"] .site-builder-button {
                background: var(--brunch-paper) !important;
            }

            [data-web-template="restaurant-brunch"] [data-document-role="heroSecondaryAction"] {
                transform: rotate(1deg);
            }

            [data-web-template="restaurant-brunch"] :where(
                [data-document-role="heroPrimaryAction"],
                [data-document-role="heroSecondaryAction"]
            ):hover {
                transform: translate(3px, 3px) rotate(0deg) !important;
            }

            [data-web-template="restaurant-brunch"] :where(
                [data-document-role="heroPrimaryAction"],
                [data-document-role="heroSecondaryAction"]
            ):hover .site-builder-button {
                box-shadow: 2px 3px 0 var(--brunch-ink) !important;
            }

            [data-web-template="restaurant-brunch"] :where(
                [data-document-role="heroHighlightOne"],
                [data-document-role="heroHighlightTwo"],
                [data-document-role="heroHighlightThree"]
            ) {
                border: 2px solid var(--brunch-ink) !important;
                box-shadow: 3px 4px 0 color-mix(in srgb, var(--brunch-ink) 18%, transparent) !important;
                color: var(--brunch-ink) !important;
                font-weight: 900 !important;
                justify-self: start;
                padding: .72rem 1rem !important;
            }

            [data-web-template="restaurant-brunch"] [data-document-role="heroHighlightOne"] {
                background: var(--brunch-peach) !important;
                transform: rotate(-3deg);
            }

            [data-web-template="restaurant-brunch"] [data-document-role="heroHighlightTwo"] {
                background: var(--brunch-mint) !important;
                transform: rotate(2deg) translateY(.25rem);
            }

            [data-web-template="restaurant-brunch"] [data-document-role="heroHighlightThree"] {
                background: var(--brunch-sky) !important;
                transform: rotate(-1deg);
            }

            [data-web-template="restaurant-brunch"] [data-document-role="heroFeature"] {
                align-content: start;
                background: var(--brunch-paper) !important;
                border: 3px solid var(--brunch-ink) !important;
                border-radius: 1.1rem !important;
                box-shadow: 18px 20px 0 var(--brunch-peach) !important;
                display: grid !important;
                grid-column: 7 / -1 !important;
                grid-row: 1 !important;
                padding: clamp(.7rem, 1.2vw, 1rem) !important;
                position: relative;
                transform: rotate(2.2deg);
                z-index: 1;
            }

            [data-web-template="restaurant-brunch"] [data-document-role="heroFeature"]::before {
                align-items: center;
                aspect-ratio: 1;
                background: var(--brunch-yolk);
                border: 3px solid var(--brunch-ink);
                border-radius: 50%;
                box-shadow: 5px 6px 0 var(--brunch-tomato);
                color: var(--brunch-ink);
                content: "GOOD\\A MORNING";
                display: flex;
                font-family: var(--brunch-display-font);
                font-size: clamp(.68rem, 1.15vw, .92rem);
                font-weight: 900;
                justify-content: center;
                letter-spacing: .08em;
                line-height: .95;
                padding: .8rem;
                position: absolute;
                right: .6rem;
                text-align: center;
                top: clamp(-2rem, -3vw, -1rem);
                transform: rotate(11deg);
                white-space: pre;
                width: clamp(5rem, 8vw, 7.2rem);
                z-index: 3;
            }

            [data-web-template="restaurant-brunch"] [data-document-role="heroFeature"] > [data-site-builder-block="image"] {
                overflow: hidden;
            }

            [data-web-template="restaurant-brunch"] [data-document-role="heroFeature"] .site-builder-image {
                aspect-ratio: 4 / 5 !important;
                border-radius: .6rem !important;
                filter: saturate(1.08) contrast(.96) brightness(1.06);
                max-height: 580px;
                object-fit: cover;
            }

            [data-web-template="restaurant-brunch"] [data-document-role="heroOpeningHours"] {
                background: transparent !important;
                gap: .65rem !important;
                margin-top: .15rem;
            }

            [data-web-template="restaurant-brunch"] [data-document-role="heroOpeningHours"] > .site-builder-column {
                background: var(--brunch-cream) !important;
                border: 2px solid var(--brunch-ink) !important;
                border-radius: .8rem !important;
                color: var(--brunch-ink) !important;
            }

            [data-web-template="restaurant-brunch"] [data-document-role="heroOpeningHours"] .site-builder-text {
                color: var(--brunch-ink) !important;
            }

            /* Shared editorial language: bold labels, offset borders and paper cards. */
            [data-web-template="restaurant-brunch"] :where(
                [data-web-region="occasions"],
                [data-web-region="menu"],
                [data-web-region="gallery"],
                [data-web-region="reservation"]
            ) .site-builder-text-h2 {
                color: var(--brunch-ink) !important;
                font-size: clamp(3.2rem, 6.2vw, 6.2rem) !important;
                line-height: .9 !important;
                text-wrap: balance;
            }

            [data-web-template="restaurant-brunch"] :where(
                [data-web-region="occasions"],
                [data-web-region="menu"],
                [data-web-region="gallery"],
                [data-web-region="reservation"]
            ) [data-site-builder-block="text"] > .site-builder-text {
                color: var(--brunch-ink) !important;
            }

            [data-web-template="restaurant-brunch"] :where(
                [data-web-region="occasions"],
                [data-web-region="menu"],
                [data-web-region="gallery"],
                [data-web-region="reservation"]
            ) [data-site-builder-block="text"]:has(.site-builder-text-body[style*="uppercase"]) .site-builder-text {
                color: var(--brunch-leaf) !important;
            }

            [data-web-template="restaurant-brunch"] [data-web-region="occasions"] {
                background:
                    radial-gradient(circle at 94% 14%, color-mix(in srgb, var(--brunch-yolk) 62%, transparent) 0 8rem, transparent 8.1rem),
                    linear-gradient(108deg, var(--brunch-peach), color-mix(in srgb, var(--brunch-peach) 50%, var(--brunch-cream))) !important;
                border-block: 3px solid var(--brunch-ink) !important;
                min-height: 700px !important;
                transform: none !important;
            }

            [data-web-template="restaurant-brunch"] [data-web-region="occasions"] > * {
                transform: none !important;
            }

            [data-web-template="restaurant-brunch"] [data-web-region="occasions"] > .site-builder-grid > .site-builder-grid-item:first-child {
                align-self: center;
                position: relative;
                transform: rotate(-3deg);
                z-index: 2;
            }

            [data-web-template="restaurant-brunch"] [data-web-region="occasions"] > .site-builder-grid > .site-builder-grid-item:first-child::after {
                align-items: center;
                background: var(--brunch-tomato);
                border: 2px solid var(--brunch-ink);
                border-radius: 50%;
                color: var(--brunch-paper);
                content: "♥";
                display: flex;
                font-size: 2rem;
                height: 5.5rem;
                justify-content: center;
                position: absolute;
                right: -1.25rem;
                top: -1.5rem;
                transform: rotate(12deg);
                width: 5.5rem;
                z-index: 3;
            }

            [data-web-template="restaurant-brunch"] [data-web-region="occasions"] .site-builder-image {
                border: .75rem solid var(--brunch-paper) !important;
                border-radius: .5rem !important;
                box-shadow: 13px 15px 0 var(--brunch-ink);
                filter: saturate(1.08) brightness(1.05);
            }

            [data-web-template="restaurant-brunch"] [data-web-region="occasions"] .site-builder-card {
                background: var(--brunch-paper) !important;
                border: 2px solid var(--brunch-ink) !important;
                border-radius: 1rem !important;
                box-shadow: 6px 7px 0 var(--brunch-ink) !important;
                overflow: visible;
            }

            [data-web-template="restaurant-brunch"] [data-web-region="occasions"] .site-builder-card:nth-of-type(3n + 1) {
                background: var(--brunch-yolk) !important;
                transform: rotate(-1deg);
            }

            [data-web-template="restaurant-brunch"] [data-web-region="occasions"] .site-builder-card:nth-of-type(3n + 2) {
                background: var(--brunch-mint) !important;
                transform: translateX(.8rem) rotate(1deg);
            }

            [data-web-template="restaurant-brunch"] [data-web-region="menu"] {
                background:
                    radial-gradient(circle at 88% 8%, var(--brunch-sky) 0 7rem, transparent 7.1rem),
                    linear-gradient(var(--brunch-cream), var(--brunch-cream)) !important;
            }

            [data-web-template="restaurant-brunch"] [data-web-region="menu"]::after {
                background: var(--brunch-yolk);
                border-block: 3px solid var(--brunch-ink);
                bottom: 1.8rem;
                content: "";
                height: clamp(3rem, 6vw, 5.5rem);
                left: -3%;
                opacity: .82;
                position: absolute;
                right: -3%;
                transform: rotate(2deg);
                z-index: 0;
            }

            [data-web-template="restaurant-brunch"] [data-web-region="menu"] > .site-builder-grid {
                position: relative;
                z-index: 1;
            }

            [data-web-template="restaurant-brunch"] [data-web-region="menu"] > .site-builder-grid > .site-builder-grid-item:nth-child(2) {
                background: var(--brunch-paper) !important;
                border: 2px solid var(--brunch-ink) !important;
                box-shadow: 5px 6px 0 var(--brunch-mint);
                transform: rotate(2deg);
            }

            [data-web-template="restaurant-brunch"] [data-web-region="menu"] .site-builder-card {
                background: var(--brunch-paper) !important;
                border: 2px solid var(--brunch-ink) !important;
                border-radius: 1rem !important;
                box-shadow: 8px 9px 0 color-mix(in srgb, var(--brunch-ink) 18%, transparent) !important;
                transform: rotate(-.75deg);
            }

            [data-web-template="restaurant-brunch"] [data-web-region="menu"] > .site-builder-grid > .site-builder-grid-item:nth-child(even) .site-builder-card {
                background: color-mix(in srgb, var(--brunch-yolk) 34%, var(--brunch-paper)) !important;
                transform: translateY(1rem) rotate(.8deg);
            }

            [data-web-template="restaurant-brunch"] [data-web-region="menu"] .site-builder-card:hover,
            [data-web-template="restaurant-brunch"] [data-web-region="menu"] .site-builder-card:focus-within {
                box-shadow: 4px 5px 0 var(--brunch-tomato) !important;
                transform: translate(3px, 3px) rotate(0deg) !important;
            }

            [data-web-template="restaurant-brunch"] [data-web-region="menu"] .site-builder-badge {
                background: var(--brunch-yolk) !important;
                border: 2px solid var(--brunch-ink) !important;
                box-shadow: 3px 4px 0 var(--brunch-tomato) !important;
                color: var(--brunch-ink) !important;
            }

            [data-web-template="restaurant-brunch"] [data-web-region="gallery"] {
                background:
                    radial-gradient(circle at 3% 82%, var(--brunch-yolk) 0 8rem, transparent 8.1rem),
                    linear-gradient(132deg, var(--brunch-mint), color-mix(in srgb, var(--brunch-sky) 58%, var(--brunch-mint))) !important;
                border-block: 3px solid var(--brunch-ink) !important;
            }

            [data-web-template="restaurant-brunch"] [data-web-region="gallery"] > .site-builder-grid > .site-builder-grid-item:has(.site-builder-image) {
                position: relative;
            }

            [data-web-template="restaurant-brunch"] [data-web-region="gallery"] .site-builder-image {
                border: .65rem solid var(--brunch-paper) !important;
                border-radius: .4rem !important;
                box-shadow: 10px 12px 0 var(--brunch-ink);
                filter: saturate(1.06) brightness(1.05);
            }

            [data-web-template="restaurant-brunch"] [data-web-region="gallery"] > .site-builder-grid > .site-builder-grid-item:nth-child(2) {
                transform: rotate(-2.1deg);
                z-index: 2;
            }

            [data-web-template="restaurant-brunch"] [data-web-region="gallery"] > .site-builder-grid > .site-builder-grid-item:nth-child(3) {
                transform: translate(-.6rem, .9rem) rotate(3deg);
                z-index: 3;
            }

            [data-web-template="restaurant-brunch"] [data-web-region="gallery"] > .site-builder-grid > .site-builder-grid-item:nth-child(4) {
                transform: translate(.5rem, -.5rem) rotate(-3deg);
                z-index: 1;
            }

            [data-web-template="restaurant-brunch"] [data-web-region="gallery"] > .site-builder-grid > .site-builder-grid-item:last-child {
                background: var(--brunch-yolk) !important;
                border: 2px solid var(--brunch-ink) !important;
                border-radius: 50% 50% 1.2rem 1.2rem !important;
                box-shadow: 9px 10px 0 var(--brunch-tomato);
                transform: rotate(1.5deg);
            }

            [data-web-template="restaurant-brunch"] [data-web-region="experience"] {
                --site-text: var(--brunch-ink);
                background:
                    radial-gradient(circle at 96% 10%, var(--brunch-yolk) 0 7rem, transparent 7.1rem),
                    linear-gradient(100deg, var(--brunch-tomato), color-mix(in srgb, var(--brunch-peach) 74%, var(--brunch-tomato))) !important;
                border-bottom: 3px solid var(--brunch-ink) !important;
                color: var(--brunch-ink) !important;
            }

            [data-web-template="restaurant-brunch"] [data-web-region="experience"] > .site-builder-grid > .site-builder-grid-item {
                background: var(--brunch-paper) !important;
                border: 2px solid var(--brunch-ink) !important;
                border-radius: 1.1rem !important;
                box-shadow: 8px 9px 0 var(--brunch-ink);
                color: var(--brunch-ink) !important;
            }

            [data-web-template="restaurant-brunch"] [data-web-region="experience"] > .site-builder-grid > .site-builder-grid-item:nth-child(2) {
                background: var(--brunch-yolk) !important;
                transform: translateY(1rem) rotate(1deg);
            }

            [data-web-template="restaurant-brunch"] [data-web-region="experience"] > .site-builder-grid > .site-builder-grid-item:nth-child(3) {
                background: var(--brunch-mint) !important;
                transform: rotate(-1deg);
            }

            [data-web-template="restaurant-brunch"] [data-web-region="experience"] :where(.site-builder-text, .site-builder-text > span, .site-builder-icon) {
                color: var(--brunch-ink) !important;
            }

            [data-web-template="restaurant-brunch"] [data-web-region="experience"] > .site-builder-grid > .site-builder-grid-item > [data-site-builder-block="text"]:first-child .site-builder-text {
                align-items: center;
                background: var(--brunch-leaf) !important;
                border: 2px solid var(--brunch-ink);
                border-radius: 50%;
                color: var(--brunch-paper) !important;
                display: inline-flex;
                height: 4.5rem;
                justify-content: center;
                transform: rotate(-7deg);
                width: 4.5rem !important;
            }

            [data-web-template="restaurant-brunch"] [data-web-region="reservation"] {
                background:
                    radial-gradient(circle at 8% 14%, var(--brunch-peach) 0 8rem, transparent 8.1rem),
                    linear-gradient(var(--brunch-cream), color-mix(in srgb, var(--brunch-mint) 40%, var(--brunch-cream))) !important;
            }

            [data-web-template="restaurant-brunch"] [data-web-region="reservation"] > .site-builder-grid > .site-builder-grid-item {
                background: var(--brunch-paper) !important;
                border: 2px solid var(--brunch-ink) !important;
                border-radius: 1.4rem !important;
                box-shadow: 10px 12px 0 var(--brunch-ink);
                color: var(--brunch-ink) !important;
            }

            [data-web-template="restaurant-brunch"] [data-web-region="reservation"] > .site-builder-grid > .site-builder-grid-item:first-child {
                transform: rotate(-.8deg);
            }

            [data-web-template="restaurant-brunch"] [data-web-region="reservation"] > .site-builder-grid > .site-builder-grid-item:last-child {
                background: var(--brunch-yolk) !important;
                transform: translateY(1.2rem) rotate(1deg);
            }

            [data-web-template="restaurant-brunch"] [data-web-region="reservation"] :where(
                .site-builder-form,
                [data-document-role="openingHoursTable"],
                [data-document-role="openingHoursTable"] + .site-builder-column
            ) {
                background: color-mix(in srgb, var(--brunch-peach) 32%, var(--brunch-paper)) !important;
                border: 2px solid var(--brunch-ink) !important;
                box-shadow: none !important;
                color: var(--brunch-ink) !important;
            }

            [data-web-template="restaurant-brunch"] [data-web-region="reservation"] :where(
                .site-builder-form-title,
                .site-builder-form-text,
                .site-builder-form-field,
                .site-template-lead-form label,
                .site-template-lead-form label > span
            ) {
                color: var(--brunch-ink) !important;
            }

            [data-web-template="restaurant-brunch"] [data-web-region="reservation"] .site-builder-form :where(input, select, textarea) {
                background: var(--brunch-paper) !important;
                border: 2px solid color-mix(in srgb, var(--brunch-ink) 78%, transparent) !important;
                border-radius: .75rem !important;
                color: var(--brunch-ink) !important;
            }

            [data-web-template="restaurant-brunch"] [data-web-region="reservation"] .site-builder-form :where(input, select, textarea):focus {
                border-color: var(--brunch-tomato) !important;
                box-shadow: 0 0 0 4px color-mix(in srgb, var(--brunch-tomato) 20%, transparent) !important;
                outline: none;
            }

            [data-web-template="restaurant-brunch"] [data-web-region="reservation"] .site-builder-form :where(
                .site-builder-form-submit .site-builder-button,
                .site-builder-form-submit-preview .site-builder-button
            ) {
                background: var(--brunch-leaf) !important;
                border: 2px solid var(--brunch-ink) !important;
                box-shadow: 4px 5px 0 var(--brunch-tomato) !important;
                color: var(--brunch-paper) !important;
            }

            [data-web-template="restaurant-brunch"] [data-web-region="footer"] {
                background:
                    radial-gradient(circle at 90% 18%, color-mix(in srgb, var(--brunch-yolk) 26%, transparent) 0 9rem, transparent 9.1rem),
                    var(--brunch-leaf) !important;
                border-color: var(--brunch-ink) !important;
                border-top: 3px solid var(--brunch-ink) !important;
            }

            [data-web-template="restaurant-brunch"] [data-web-region="footer"] :where(.site-builder-text, .site-builder-text > span, .site-builder-icon) {
                color: var(--brunch-paper) !important;
            }

            [data-web-template="restaurant-brunch"] [data-web-region="footer"] a:hover :where(.site-builder-text, .site-builder-text > span) {
                color: var(--brunch-yolk) !important;
            }

            [data-web-template="restaurant-brunch"] main:not(:has([data-web-region])) {
                background:
                    radial-gradient(circle at 92% 8%, var(--brunch-peach) 0 8rem, transparent 8.1rem),
                    var(--brunch-cream) !important;
                color: var(--brunch-ink) !important;
                padding-top: 10rem !important;
            }

            [data-web-template="restaurant-brunch"] main:not(:has([data-web-region])) article {
                background: var(--brunch-paper) !important;
                border: 2px solid var(--brunch-ink) !important;
                box-shadow: 12px 14px 0 var(--brunch-yolk) !important;
                color: var(--brunch-ink) !important;
            }

            [data-web-template="restaurant-brunch"] main:not(:has([data-web-region])) article :where(h1, h2, h3, strong) {
                color: var(--brunch-ink) !important;
            }

            @media (max-width: 1100px) {
                [data-web-template="restaurant-brunch"] [data-web-region="hero"] {
                    min-height: 820px !important;
                }

                [data-web-template="restaurant-brunch"] [data-document-role="heroHeading"] .site-builder-text {
                    font-size: clamp(4rem, 8.5vw, 7rem) !important;
                }

                [data-web-template="restaurant-brunch"] [data-document-role="heroFeature"] {
                    box-shadow: 12px 14px 0 var(--brunch-peach) !important;
                }
            }

            @media (max-width: 900px) {
                [data-web-template="restaurant-brunch"] [data-web-region="header"],
                [data-web-template="restaurant-brunch"] [data-web-region="header"][data-restaurant-header-scrolled] {
                    min-height: 72px !important;
                }

                [data-web-template="restaurant-brunch"] [data-web-region="header"] .restaurant-mobile-menu-toggle {
                    background: var(--brunch-yolk) !important;
                    border: 2px solid var(--brunch-ink) !important;
                    box-shadow: 3px 3px 0 var(--brunch-tomato);
                    opacity: 1 !important;
                    pointer-events: auto !important;
                    transform: none !important;
                }

                [data-web-template="restaurant-brunch"] [data-web-region="header"] .restaurant-mobile-menu-toggle > span {
                    background: var(--brunch-ink) !important;
                }

                [data-web-template="restaurant-brunch"] [data-web-region="header"][data-restaurant-mobile-menu-open] {
                    background:
                        radial-gradient(circle at 88% 8%, var(--brunch-yolk) 0 8rem, transparent 8.1rem),
                        var(--brunch-cream) !important;
                }

                [data-web-template="restaurant-brunch"] [data-web-region="header"][data-restaurant-mobile-menu-open] [data-document-role="headerNavigation"] .site-builder-text > span {
                    color: var(--brunch-ink) !important;
                }

                [data-web-template="restaurant-brunch"] [data-web-region="hero"] {
                    min-height: 0 !important;
                    padding: 9.5rem 1.25rem 7rem !important;
                }

                [data-web-template="restaurant-brunch"] [data-web-region="hero"] > .site-builder-grid {
                    grid-template-columns: minmax(0, 1fr) !important;
                    row-gap: 4.5rem !important;
                }

                [data-web-template="restaurant-brunch"] [data-web-region="hero"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="heroCopy"]),
                [data-web-template="restaurant-brunch"] [data-web-region="hero"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="heroFeature"]) {
                    grid-column: 1 !important;
                }

                [data-web-template="restaurant-brunch"] [data-web-region="hero"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="heroCopy"]) {
                    grid-row: 1 !important;
                }

                [data-web-template="restaurant-brunch"] [data-web-region="hero"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="heroFeature"]) {
                    grid-row: 2 !important;
                }

                [data-web-template="restaurant-brunch"] [data-document-role="heroCopy"],
                [data-web-template="restaurant-brunch"] [data-document-role="heroFeature"] {
                    grid-column: 1 !important;
                }

                [data-web-template="restaurant-brunch"] [data-document-role="heroCopy"] {
                    grid-row: 1 !important;
                    transform: none;
                }

                [data-web-template="restaurant-brunch"] [data-document-role="heroFeature"] {
                    grid-row: 2 !important;
                    justify-self: center;
                    max-width: 36rem;
                    transform: rotate(1.5deg);
                    width: min(92%, 36rem);
                }

                [data-web-template="restaurant-brunch"] [data-document-role="heroHeading"] .site-builder-text {
                    font-size: clamp(4rem, 15vw, 7rem) !important;
                    max-width: 44rem !important;
                }
            }

            @media (max-width: 767px) {
                [data-web-template="restaurant-brunch"] .site-builder-section {
                    padding-inline: 1rem !important;
                }

                [data-web-template="restaurant-brunch"] :where(
                    [data-web-region="occasions"],
                    [data-web-region="menu"],
                    [data-web-region="gallery"],
                    [data-web-region="reservation"]
                ) .site-builder-text-h2 {
                    font-size: clamp(2.8rem, 13vw, 4.7rem) !important;
                }

                [data-web-template="restaurant-brunch"] [data-web-region="occasions"] > .site-builder-grid > .site-builder-grid-item:first-child,
                [data-web-template="restaurant-brunch"] [data-web-region="gallery"] > .site-builder-grid > .site-builder-grid-item,
                [data-web-template="restaurant-brunch"] [data-web-region="reservation"] > .site-builder-grid > .site-builder-grid-item {
                    transform: none !important;
                }

                [data-web-template="restaurant-brunch"] [data-web-region="experience"] > .site-builder-grid > .site-builder-grid-item {
                    transform: none !important;
                }

                [data-web-template="restaurant-brunch"] [data-web-region="experience"] > .site-builder-grid > .site-builder-grid-item + .site-builder-grid-item {
                    margin-top: .75rem;
                }
            }

            @media (max-width: 640px) {
                [data-web-template="restaurant-brunch"] [data-web-region="hero"] {
                    background:
                        radial-gradient(circle at 90% 14%, var(--brunch-peach) 0 5rem, transparent 5.1rem),
                        linear-gradient(118deg, var(--brunch-cream), color-mix(in srgb, var(--brunch-yolk) 13%, var(--brunch-cream))) !important;
                    padding-inline: 1rem !important;
                }

                [data-web-template="restaurant-brunch"] [data-document-role="heroCopy"] {
                    gap: 1.1rem !important;
                }

                [data-web-template="restaurant-brunch"] [data-document-role="heroHeading"] .site-builder-text {
                    font-size: clamp(3.55rem, 17vw, 5.5rem) !important;
                    line-height: .86 !important;
                }

                [data-web-template="restaurant-brunch"] [data-document-role="heroPrimaryAction"],
                [data-web-template="restaurant-brunch"] [data-document-role="heroSecondaryAction"] {
                    justify-self: stretch !important;
                    width: 100% !important;
                }

                [data-web-template="restaurant-brunch"] :where(
                    [data-document-role="heroHighlightOne"],
                    [data-document-role="heroHighlightTwo"],
                    [data-document-role="heroHighlightThree"]
                ) {
                    justify-self: stretch !important;
                    text-align: center;
                    transform: none !important;
                    width: 100% !important;
                }

                [data-web-template="restaurant-brunch"] [data-document-role="heroFeature"] {
                    box-shadow: 8px 9px 0 var(--brunch-peach) !important;
                    width: calc(100% - 1rem);
                }

                [data-web-template="restaurant-brunch"] [data-document-role="heroFeature"]::before {
                    right: .35rem;
                    top: -2.4rem;
                    width: 4.9rem;
                }

                [data-web-template="restaurant-brunch"] [data-web-region="occasions"] .site-builder-card:nth-of-type(n),
                [data-web-template="restaurant-brunch"] [data-web-region="menu"] .site-builder-card:nth-of-type(n) {
                    transform: none !important;
                }
            }

            @media (prefers-reduced-motion: reduce) {
                [data-web-template="restaurant-brunch"] * {
                    scroll-behavior: auto !important;
                    transition-duration: .01ms !important;
                }
            }
        `}</style>
    );
}

export const restaurantBrunchTemplate: SiteTemplateDefinition = {
    manifest: {
        ...restaurantModernTemplate.manifest,
        code: "restaurant-brunch",
        name: "Brunch",
        description: "Svěží šablona pro brunchová bistra se snídaněmi, sezónní nabídkou a rezervací stolu.",
        previewImage: "/templates/restaurant-brunch/preview.webp",
        defaults: {
            ...restaurantModernTemplate.manifest.defaults,
            colors: {
                primary: "#34744d",
                accent: "#f5c84c",
                background: "#fff7e6",
                surface: "#fffcf5",
                text: "#213b2d",
                muted: "#647267",
                border: "#d9c8a9",
            },
            typography: {
                heading: "ui-rounded, Arial Rounded MT Bold, Trebuchet MS, system-ui, sans-serif",
                body: "Avenir Next, Inter, system-ui, sans-serif",
            },
            layout: {
                container: "1280px",
                sectionSpacing: "spacious",
                radius: "28px",
            },
            components: {
                header: "builder",
                footer: "builder",
                buttons: "pill",
                forms: "card",
            },
        },
    },
    render: (props: TemplateRenderProps) => renderRestaurantTemplate(
        restaurantBrunchTemplate,
        props,
        <BrunchStyles />,
    ),
};
